import {
  ReactElement,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import cc from 'classcat';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy } from 'utils/generic/lazy';
import { isMobile } from 'utils/is-mobile';
import { EEventsTypes } from 'interfaces/events';
import {
  EGamePerView,
  EMobileCustomSettings,
  ESliderDefaultType,
  ESliderType,
  TSelectedGamesProps
} from 'interfaces/selected-games';
import { Game } from 'interfaces/sportsbook-data-levels';
import { ListViewSkeleton } from './ListViewSkeleton';
import './styles.less';
import { CardViewSkeleton } from 'newcomponents/UI/SelectedSport/CardViewSkeleton';
import { useBoostedOdds } from 'hooks/sportsbook/useBoostedOdds';
import { useGetSelectedGames } from 'hooks/sportsbook/useGetSelectedGames';
import { getIsDarkMode } from 'store/selectors/app-data';
import { getSelectedEventsType } from 'store/selectors/events';

if (SpringConfigs.IS_RTL) {
  import('./rtl.less');
}

const { SelectedSportMockedDataManager } = lazy(
  () =>
    import(
      /* webpackChunkName: "SelectedSportMockedDataManager" */ 'components/SelectedGames/_partials/MockedDataManager'
    )
);

const { CardView } = lazy(
  () =>
    import(
      /* webpackChunkName: "card-view" */ 'newcomponents/UI/SelectedSport/CardView'
    )
);

const { ListView } = lazy(
  () => import(/* webpackChunkName: "list-view" */ './_partials/ListView')
);

export const SelectedGames = (props: TSelectedGamesProps): ReactElement => {
  useBoostedOdds();
  const [gameData, setGameData] = useState<null | Game[]>(null);
  const [sliderData, setSliderData] = useState<
    (Game & { sportName: string })[]
  >([]);

  const selectedEventsType = useSelector(getSelectedEventsType)[
    props.configs.moduleId || ''
  ] as EEventsTypes;

  const [fetchRandom, setFetchRandom] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const isDarkMode = useSelector(getIsDarkMode);
  const isListView = +props?.configs?.layoutType === 2;
  const gameIdsLength = Object.values(props.configs?.gameIds || {}).length;
  const orderStartTime = props?.configs?.orderBy === 2;
  const needFetch = +props.configs.showGamesAfterSuspension === 1;
  const gamesPerView = isMobile() ? 1 : props.configs.gamesPerView;
  const autoplayDelay =
    +props.configs.selectedSportAutoSlider &&
    props.configs.selectedSportAutoSliderTime;

  const limit = useMemo(() => {
    if (isListView) {
      return props.configs.limit > gameIdsLength
        ? gameIdsLength
        : props.configs.limit;
    } else {
      return 0;
    }
  }, [props.configs.limit, isListView]);

  const getData = useGetSelectedGames({
    setGameData,
    limit,
    orderStartTime
  });

  const ids = props.configs.gameIds;

  const sortGames = useCallback(
    (gameId?: number) => {
      const data: typeof sliderData = [];

      for (const game in gameData) {
        const id = gameData[+game]?.id;

        if (
          (typeof gameId === 'number' &&
            id === props.configs.gameIds[gameId]) ||
          (orderStartTime && props.configs.gameIds.includes(id)) ||
          fetchRandom ||
          SpringConfigs.MOCKED_DATA
        ) {
          const gameEvents = gameData[+game] as (typeof sliderData)[0];

          if (gameEvents) {
            if (isListView) {
              data.push(gameEvents);
            } else {
              const mockedDataImageUrl =
                SpringConfigs.MOCKED_DATA &&
                document
                  .querySelector('[data-widget="SelectedSport"]')
                  ?.getAttribute('data-imgurl');

              if (
                (mockedDataImageUrl || SpringConfigs.USER_FILES_URL) &&
                props.configs.imgPath[id]
              ) {
                const publicImageUrl = `${SpringConfigs.USER_FILES_URL}/media/`;
                gameEvents.imgPath = `${mockedDataImageUrl || publicImageUrl}${
                  props.configs.imgPath[id]
                }`;
              }

              data.push(gameEvents);
            }
          }
        }
      }

      return data;
    },
    [gameData, props.configs.imgPath]
  );

  useEffect(() => {
    if (
      !SpringConfigs.MOCKED_DATA &&
      (gameData?.length === 0 || ids?.length === 0)
    ) {
      setFetchRandom(needFetch);

      if (needFetch) {
        getData(Object.values(ids), true);
      }
    }
  }, [gameData?.length]);

  useEffect(() => {
    if (props.configs && !SpringConfigs.MOCKED_DATA) {
      getData(Object.values(ids));
    }
  }, [props.configs, getData, limit]);

  useEffect(() => {
    if (gameData) {
      let data: typeof sliderData = [];

      if (orderStartTime || fetchRandom || SpringConfigs.MOCKED_DATA) {
        data = sortGames();
      } else {
        for (const gameId in props.configs.gameIds) {
          const sortedData = sortGames(+gameId);

          if (sortedData.length) {
            data.push(...sortedData);
          }
        }
      }

      if (!isListView) {
        const group: any[] = [];

        for (let i = 0, j = 0; i < data.length; i++) {
          if (i >= gamesPerView && i % gamesPerView === 0) {
            j++;
          }

          group[j] = group[j] || [];
          group[j].push(data[i]);
        }

        setSliderData(group);
      } else {
        setSliderData(data);
      }
    }
  }, [gameData, gamesPerView, fetchRandom]);

  const colorVariables = useMemo(
    () => ({
      oddBorderColor:
        props.configs[`oddBorderColor${isDarkMode ? 'Dark' : ''}`] || '',
      oddsBgColor:
        props.configs[`oddsBgColor${isDarkMode ? 'Dark' : ''}`] || '',
      oddTextColor:
        props.configs[`oddTextColor${isDarkMode ? 'Dark' : ''}`] || '',
      infoTextColor:
        props.configs[`infoTextColor${isDarkMode ? 'Dark' : ''}`] || '',
      layerColor: props.configs[`layerColor${isDarkMode ? 'Dark' : ''}`] || ''
    }),
    [props.configs, isDarkMode]
  );

  if (!gameData?.length && !needFetch && !SpringConfigs.MOCKED_DATA) {
    if (gameData && ref?.current) {
      ref.current
        ?.closest('.module-container.ModuleSbSelectedSportGames')
        ?.remove();
    }

    return <div ref={ref} />;
  }

  const cssVars = `
  .selectedSportsModule_${props.configs.moduleId} {
    --SS-card-aspect-ratio: ${props.configs.aspectRatio || 0}%;
     ${
       colorVariables.layerColor
         ? `--SS-card-layer-background: ${colorVariables.layerColor};`
         : ''
     }
    ${
      colorVariables.infoTextColor
        ? ` --SS-card-text-color: ${colorVariables.infoTextColor};`
        : ''
    }
     ${
       colorVariables.oddTextColor
         ? `--SS-card-odd-text-color: ${colorVariables.oddTextColor};`
         : ''
     }
     ${
       colorVariables.oddsBgColor
         ? `--SS-card-odd-background: ${colorVariables.oddsBgColor};  --SS-card-odd-hover-background: ${colorVariables.oddsBgColor};`
         : ''
     }
    ${
      colorVariables.oddBorderColor
        ? `--SS-card-odd-border-color: ${colorVariables.oddBorderColor}; --SS-card-odd-hover-border: ${colorVariables.oddBorderColor};`
        : ''
    } 
  }`;

  const oneAndHalf =
    props.configs?.gamePerViewForMobile === EGamePerView.oneAndHalf &&
    props.configs?.mobileCustomSettings === EMobileCustomSettings.on &&
    isMobile();

  const needTitle = !!(
    (props.configs.sliderType === +ESliderType.titleWithArrows &&
      (!props.configs?.mobileCustomSettings || !isMobile())) ||
    (props.configs?.mobileCustomSettings &&
      props.configs?.sliderTypeForMobile === +ESliderType.titleWithArrows &&
      isMobile())
  );

  return (
    <>
      <style>{cssVars}</style>

      {SpringConfigs.MOCKED_DATA ? (
        <Suspense>
          <SelectedSportMockedDataManager
            setGameData={setGameData}
            limit={limit}
            fetchRandom={needFetch}
            games={gameData}
            moduleId={props.configs.moduleId}
            orderStartTime={orderStartTime}
          />
        </Suspense>
      ) : null}

      {gameData ? (
        isListView ? (
          <Suspense
            fallback={
              <div
                className={cc([
                  'event-wrapper',
                  {
                    'event-wrapper--mobile': isMobile(),
                    'event-wrapper--web': !isMobile()
                  }
                ])}
              >
                <ListViewSkeleton limit={limit} />
              </div>
            }
          >
            <ListView
              sliderData={sliderData}
              showTitle={props.configs?.showTitle}
            />
          </Suspense>
        ) : (
          <Suspense
            fallback={
              <CardViewSkeleton
                aspectRatio={props.configs.aspectRatio}
                coverMode={props.configs.coverMode}
                moduleId={props.configs.moduleId}
                cardLayoutsType={props.configs.cardLayoutsType}
                limit={props.configs.limit}
                gamesPerView={gamesPerView}
                selectedSport
                needTitle={needTitle}
                mobileSideOneAndHalf={oneAndHalf}
              />
            }
          >
            <div
              className={cc([
                'selectedSport__cardView__container',
                {
                  'selectedSport__cardView__container--mobile': isMobile(),
                  selectedGames__oneAndHalf: oneAndHalf,
                  selectedSport__cardView__withoutTitle: !needTitle
                }
              ])}
            >
              {needTitle ? (
                <div
                  className={cc([
                    'selectedSport__title',
                    { 'selectedSport__title--mobile': isMobile() }
                  ])}
                >
                  {t('sportsbook.selectedGames')}
                </div>
              ) : null}
              <CardView
                aspectRatio={props.configs.aspectRatio}
                coverMode={props.configs.coverMode}
                moduleId={props.configs.moduleId}
                cardLayout={props.configs.cardLayoutsType}
                limit={props.configs.limit}
                sliderType={
                  props.configs?.mobileCustomSettings &&
                  props.configs.sliderTypeForMobile === ESliderType.dots &&
                  isMobile()
                    ? ESliderDefaultType.titleWithArrows
                    : ESliderDefaultType.arrows
                }
                selectedSport
                selectedEventsType={selectedEventsType}
                gameData={gameData}
                sliderData={sliderData}
                gamesPerView={gamesPerView}
                showSportIcon
                autoplayDelay={autoplayDelay}
                alwaysShowArrows={gameData.length > gamesPerView}
                colorVariables={colorVariables}
                oneAndHalf={oneAndHalf}
                noNeedbodyArrows={
                  (props.configs?.mobileCustomSettings &&
                    props.configs?.sliderTypeForMobile ===
                      ESliderType.horizontalScroll &&
                    isMobile()) ||
                  false
                }
              />
            </div>
          </Suspense>
        )
      ) : isListView ? (
        <div
          className={cc([
            'event-wrapper',
            {
              'event-wrapper--mobile': isMobile(),
              'event-wrapper--web': !isMobile()
            }
          ])}
        >
          <ListViewSkeleton limit={limit} />
        </div>
      ) : (
        <CardViewSkeleton
          aspectRatio={props.configs.aspectRatio}
          coverMode={props.configs.coverMode}
          moduleId={props.configs.moduleId}
          cardLayoutsType={props.configs.cardLayoutsType}
          gamesPerView={gamesPerView}
          limit={props.configs.limit}
          selectedSport
          needTitle={needTitle}
          mobileSideOneAndHalf={oneAndHalf}
        />
      )}
    </>
  );
};
